import { renderPythonFormatTemplate } from '@apps/web/lib/python_format_template';

export type FillShiftTemplateField =
    | 'fill_shift_template_multi_assigned'
    | 'fill_shift_template_recurring_assigned'
    | 'fill_shift_template_multi_not_assigned'
    | 'fill_shift_item_template'
    | 'fill_shift_item_template_recurring'
    | 'fill_shift_item_template_not_assigned'
    | 'fill_shift_item_template_not_assigned_recurring';

export type FillShiftTemplateFieldDefinition = {
    readonly key: FillShiftTemplateField;
    readonly label: string;
    readonly description: string;
    readonly rows: number;
    readonly kind: 'message' | 'shift_item';
};

export const FILL_SHIFT_TEMPLATE_FIELDS: ReadonlyArray<FillShiftTemplateFieldDefinition> =
    [
        {
            key: 'fill_shift_template_multi_assigned',
            label: 'Assigned (open shift)',
            description:
                'Sent to the caregiver assigned to open shifts, regardless of the number of shifts.',
            rows: 3,
            kind: 'message',
        },
        {
            key: 'fill_shift_template_recurring_assigned',
            label: 'Assigned (recurring shift)',
            description:
                'Sent to the caregiver accepting a recurring shift going forward.',
            rows: 3,
            kind: 'message',
        },
        {
            key: 'fill_shift_template_multi_not_assigned',
            label: 'Not assigned',
            description:
                'Sent to caregivers who were not assigned when shifts are filled.',
            rows: 3,
            kind: 'message',
        },
        {
            key: 'fill_shift_item_template',
            label: 'Assigned shift list format (open shift)',
            description:
                'Controls how each shift is formatted inside {shift_list} for assigned open-shift notifications.',
            rows: 2,
            kind: 'shift_item',
        },
        {
            key: 'fill_shift_item_template_recurring',
            label: 'Assigned shift list format (recurring shift)',
            description:
                'Controls how each shift is formatted inside {shift_list} for assigned recurring-shift notifications.',
            rows: 2,
            kind: 'shift_item',
        },
        {
            key: 'fill_shift_item_template_not_assigned',
            label: 'Not assigned shift list format (open shift)',
            description:
                'Controls how each shift is formatted inside {shift_list} for not-assigned open-shift notifications.',
            rows: 2,
            kind: 'shift_item',
        },
        {
            key: 'fill_shift_item_template_not_assigned_recurring',
            label: 'Not assigned shift list format (recurring shift)',
            description:
                'Controls how each shift is formatted inside {shift_list} for not-assigned recurring-shift notifications.',
            rows: 2,
            kind: 'shift_item',
        },
    ];

export const DEFAULT_FILL_SHIFT_ITEM_TEMPLATE =
    '{shift_date}, {shift_start_time} to {shift_end_time} {patient_text} {location_text}';
export const DEFAULT_FILL_SHIFT_ITEM_TEMPLATE_RECURRING =
    '{shift_day_of_week}s, starting {shift_date}, {shift_start_time} to {shift_end_time} ' +
    '{patient_text} {location_text}';
export const NOT_ASSIGNED_FILL_SHIFT_ITEM_TEMPLATE =
    '{shift_date}, {shift_start_time} to {shift_end_time} {location_text}';
export const NOT_ASSIGNED_FILL_SHIFT_ITEM_TEMPLATE_RECURRING =
    '{shift_day_of_week}s, starting {shift_date}, {shift_start_time} to {shift_end_time} ' +
    '{location_text}';

export const ORGANIZED_FILL_SHIFT_ASSIGNED_TEMPLATE =
    "Hi {caregiver_first_name}, you're confirmed.\n\nSchedule:\n" +
    '{shift_list}\n\nCare plan summary:\n{care_plan_summary}\n\n' +
    'Next step:\nLog into {ehr} and review the full care plan. Let us know ' +
    'if you have any questions.';

// Preview-side copy of the worker default used for shift-discovery fills.
export const SHIFT_DISCOVERY_FILLED_ASSIGNED_TEMPLATE =
    "Hi {caregiver_first_name}, great news — you're confirmed for the " +
    'shift(s) you asked about:\n{shift_list}\nThanks for picking this up! ' +
    'Reply here if anything changes.';

export function renderFillShiftTemplate(
    template: string,
    values: Readonly<Record<string, string>>,
): string {
    try {
        return renderPythonFormatTemplate(template, values, 'empty');
    } catch {
        return template;
    }
}

export function renderFillShiftItemTemplate(
    template: string,
    values: Readonly<Record<string, string>>,
    fallbackTemplate: string = DEFAULT_FILL_SHIFT_ITEM_TEMPLATE,
): string {
    let rendered: string;
    try {
        rendered = renderPythonFormatTemplate(template, values, 'error');
    } catch {
        rendered = renderPythonFormatTemplate(
            fallbackTemplate,
            values,
            'error',
        );
    }
    const normalizedLines = rendered
        .split('\n')
        .map((line) => line.trim().split(/\s+/).join(' '))
        .filter((line) => line.length > 0);
    return normalizedLines.length > 0 ? normalizedLines.join('\n') : '';
}
